import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  RichTextEditorComponent,
  Inject,
  Toolbar,
  Link,
  HtmlEditor,
  Image,
} from '@syncfusion/ej2-react-richtexteditor'
import { IContact } from '@dataTypes/contact.type'
import { EmailAttachmentMeta, EmailComposerDraft } from '@dataTypes/email.type'
import { useAppSelector } from '@store/hooks'
import { normalizeBrowserSelectionInRteEditPanel } from '@utils/rteEmailSelection.util'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'
import {
  requestEmailSettings,
  type EmailSettingsData,
} from '@services/api/email.api'
import { useAiTextAssist, aiTextActions } from '@hooks/useAiTextAssist'
import Button from '@components/common/Button/Button.component'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { MenuEventArgs } from '@syncfusion/ej2-react-navigations'
import {
  RichTextEditor,
  type RichTextEditorFontSize,
  type RichTextEditorToolbarItemId,
} from '@components/ui'
import type { TextAlignment } from '@components/ui/RichTextEditor/useRichTextEditor'
import { ReactComponent as WriteAiIcon } from '@assets/images/Icons/Write_ai.svg'
import { ReactComponent as EmojiSmileIcon } from '@assets/images/Icons/Emoji_smile.svg'
import { ReactComponent as SendFillIcon } from '@assets/images/Icons/Send.svg'
import { ReactComponent as AttachmentIcon } from '@assets/images/Icons/Attachment.svg'
import { logger } from '@utils/safeLogger'

import './EmailComposeForm.css'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const BODY_MAX_CHARS = 1000
const AI_SLASH_TRIGGER = '/'
const AI_PROMPT_PLACEHOLDER = 'Ask AI anything'
const AI_REFINE_PLACEHOLDER = 'Type here to paraphrase with AI'
const AI_SPELLING_GRAMMAR_ACTION = {
  key: 'grammar_check' as const,
  label: 'Spelling & grammar',
  icon: 'ti ti-language',
}

const AI_ENHANCE_ACTIONS = [
  ...aiTextActions.filter((action) =>
    ['formalize', 'summarize', 'list_items', 'simplify'].includes(action.key),
  ),
  AI_SPELLING_GRAMMAR_ACTION,
]
const AI_ENHANCE_DROPDOWN_ITEMS = AI_ENHANCE_ACTIONS.map((action) => ({
  text: action.label,
}))
const AI_EXTRA_ACTIONS = [AI_SPELLING_GRAMMAR_ACTION]

type RteCommandName = Parameters<RichTextEditorComponent['executeCommand']>[0]
type RteCommandValue = Parameters<RichTextEditorComponent['executeCommand']>[1]

function stripHtmlForCount(html: string): string {
  if (typeof document === 'undefined') {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
  const div = document.createElement('div')
  div.innerHTML = html
  const text = div.textContent ?? div.innerText ?? ''
  return text.replace(/\s+/g, ' ').trim()
}

function stripSignatureFromPlainText(
  text: string,
  signatureHtml?: string | null,
): string {
  if (!text?.trim() || !signatureHtml?.trim()) return text
  const signaturePlain = stripHtmlForCount(signatureHtml)
  if (!signaturePlain) return text
  if (!text.includes(signaturePlain)) return text
  return text.replace(signaturePlain, ' ').replace(/\s+/g, ' ').trim()
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function aiPreviewHtmlToPlain(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .trim()
}

function plainTextHasGreeting(text: string): boolean {
  const firstLine = text.trim().split(/\n/)[0]?.trim().toLowerCase() ?? ''
  return /^(hi|hello|dear)\b/.test(firstLine)
}

function normalizeAiGreetingAndBody(text: string): {
  greeting: string | null
  body: string
} {
  const trimmed = text.trim()
  if (!trimmed) return { greeting: null, body: '' }

  const capitalizeSalutation = (word: string): string =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

  const inlineWithName = trimmed.match(
    /^(hi|hello|dear)\s+([^,\n—–-]+?)\s*[,—–-]\s*(.+)$/i,
  )
  if (inlineWithName) {
    const name = inlineWithName[2].trim()
    return {
      greeting: `${capitalizeSalutation(inlineWithName[1])} ${name},`,
      body: inlineWithName[3].trim(),
    }
  }

  const inlineNoName = trimmed.match(/^(hi|hello|dear)\s*[,—–-]\s*(.+)$/i)
  if (inlineNoName) {
    return {
      greeting: `${capitalizeSalutation(inlineNoName[1])},`,
      body: inlineNoName[2].trim(),
    }
  }

  const lines = trimmed.split(/\n/)
  const firstLine = lines[0]?.trim() ?? ''
  if (/^(hi|hello|dear)\b/i.test(firstLine) && lines.length > 1) {
    const rest = lines.slice(1).join('\n').trim()
    if (rest) {
      const greeting = firstLine.endsWith(',') ? firstLine : `${firstLine},`
      return { greeting, body: rest }
    }
  }

  return { greeting: null, body: trimmed }
}

function resolveRecipientGreetingName(
  recipientEmails: string[],
  contacts: IContact[],
): string | null {
  const email = recipientEmails[0]?.trim()
  if (!email) return null

  const contact = contacts.find(
    (c) => c.email?.toLowerCase() === email.toLowerCase(),
  )
  if (contact) {
    const preferred = contact.preferred_name?.trim()
    if (preferred) return preferred.split(/\s+/)[0]
    const first = contact.first_name?.trim()
    if (first) return first
  }

  const localPart = email.split('@')[0] ?? ''
  const token = localPart.split(/[._+-]/).find((part) => part.length > 0)
  if (!token) return null
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
}

function formatEmailBodyFromAiInsert(options: {
  aiPlainText: string
  recipientEmails: string[]
  contacts: IContact[]
  signatureHtml: string
  includeGreeting: boolean
}): string {
  let bodyText = options.aiPlainText.trim()
  const parts: string[] = []

  const { greeting: aiGreeting, body: aiBody } =
    normalizeAiGreetingAndBody(bodyText)
  if (aiGreeting) {
    parts.push(`<p>${escapeHtml(aiGreeting)}</p>`)
    parts.push('<p><br></p>')
    bodyText = aiBody
  } else if (options.includeGreeting && !plainTextHasGreeting(bodyText)) {
    const name = resolveRecipientGreetingName(
      options.recipientEmails,
      options.contacts,
    )
    parts.push(`<p>${name ? `Hi ${escapeHtml(name)},` : 'Hi,'}</p>`)
    parts.push('<p><br></p>')
  }

  const paragraphs = bodyText
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
  if (paragraphs.length === 0 && bodyText) {
    paragraphs.push(bodyText)
  }
  paragraphs.forEach((paragraph) => {
    const inner = escapeHtml(paragraph).replace(/\n/g, '<br>')
    parts.push(`<p>${inner}</p>`)
  })

  const signature = options.signatureHtml.trim()
  if (signature) {
    parts.push('<p><br></p>')
    parts.push('<p><br></p>')
    parts.push(signature)
  }

  return parts.join('')
}

function focusRte(rte: RichTextEditorComponent): void {
  const r = rte as RichTextEditorComponent & { focusIn?: () => void }
  if (typeof r.focusIn === 'function') {
    r.focusIn()
  }
}

type ComposeAssistSettings = Pick<
  EmailSettingsData,
  'spelling_suggestions' | 'autocorrect_support'
>

const DEFAULT_COMPOSE_ASSIST: ComposeAssistSettings = {
  spelling_suggestions: true,
  autocorrect_support: true,
}

function applyComposeAssistAttributes(
  element: HTMLElement,
  settings: ComposeAssistSettings,
): void {
  const spellEnabled = Boolean(settings.spelling_suggestions)
  const spellValue = spellEnabled ? 'true' : 'false'
  const autocorrectValue = settings.autocorrect_support ? 'on' : 'off'
  const autocapitalizeValue = settings.autocorrect_support ? 'sentences' : 'off'

  if (element.lang !== 'en') {
    element.lang = 'en'
    element.setAttribute('lang', 'en')
  }
  if (element.spellcheck !== spellEnabled) {
    element.spellcheck = spellEnabled
  }
  if (element.getAttribute('spellcheck') !== spellValue) {
    element.setAttribute('spellcheck', spellValue)
  }
  if (element.getAttribute('autocorrect') !== autocorrectValue) {
    element.setAttribute('autocorrect', autocorrectValue)
  }
  if (element.getAttribute('autocapitalize') !== autocapitalizeValue) {
    element.setAttribute('autocapitalize', autocapitalizeValue)
  }
}

function normalizeComposeAssistSettings(
  settings: EmailSettingsData | null,
): ComposeAssistSettings {
  if (!settings) return DEFAULT_COMPOSE_ASSIST
  return {
    spelling_suggestions: Boolean(settings.spelling_suggestions),
    autocorrect_support: Boolean(settings.autocorrect_support),
  }
}

function applyComposeAssistToRte(
  rte: RichTextEditorComponent,
  settings: ComposeAssistSettings,
): boolean {
  let applied = false
  const panel = rte.contentModule?.getEditPanel?.() as HTMLElement | undefined
  if (panel) {
    applyComposeAssistAttributes(panel, settings)
    applied = true
  }

  const root = rte.element
  if (root) {
    root
      .querySelectorAll<HTMLElement>('.e-rte-content, .e-content')
      .forEach((el) => {
        applyComposeAssistAttributes(el, settings)
        applied = true
      })
    root.querySelectorAll('iframe').forEach((iframe) => {
      const doc = iframe.contentDocument
      doc?.documentElement?.setAttribute('lang', 'en')
      const body = doc?.body
      if (body) {
        applyComposeAssistAttributes(body, settings)
        applied = true
      }
    })
  }

  return applied
}

type RecipientChipRowHandle = {
  focus: () => void
}

type RecipientChipRowProps = {
  label: string
  emails: string[]
  onChange: (next: string[]) => void
  placeholder: string
  /** Renders after the chip field on the same row (e.g. CC / BCC toggles). */
  trailing?: React.ReactNode
  /** Fires when the input gains focus, so the parent can track the active row. */
  onFocus?: () => void
}

const RecipientChipRow = React.forwardRef<
  RecipientChipRowHandle,
  RecipientChipRowProps
>(function RecipientChipRow(
  { label, emails, onChange, placeholder, trailing, onFocus },
  ref,
): React.ReactElement {
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus()
    },
  }))

  const addEmail = (raw: string): void => {
    const trimmed = raw.trim().replace(/,$/, '')
    if (!trimmed || !emailRegex.test(trimmed)) return
    if (emails.includes(trimmed)) return
    onChange([...emails, trimmed])
    setInput('')
  }

  const removeAt = (index: number): void => {
    onChange(emails.filter((_, i) => i !== index))
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      if (input.trim()) {
        e.preventDefault()
        addEmail(input)
      }
    } else if (e.key === 'Backspace' && !input && emails.length > 0) {
      removeAt(emails.length - 1)
    }
  }

  return (
    <div className="ecf-recipient-row">
      <span className="ecf-field-label">{label}</span>
      <div className="ecf-chip-field">
        <div className="ecf-chip-inner">
          {emails.map((em, idx) => (
            <span key={`${em}-${idx}`} className="ecf-chip">
              <span className="ecf-chip-text">{em}</span>
              <button
                type="button"
                className="ecf-chip-remove"
                aria-label={`Remove ${em}`}
                onClick={() => removeAt(idx)}
              >
                <i className="mgc mgc_close_circle_fill" aria-hidden />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            className="ecf-chip-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={() => {
              if (input.trim()) addEmail(input)
            }}
            placeholder={emails.length === 0 ? placeholder : ''}
            aria-label={placeholder}
          />
        </div>
      </div>
      {trailing}
    </div>
  )
})

type ContactPickerProps = {
  contacts: IContact[]
  /** Emails already on this row, hidden from the list to prevent duplicates. */
  excludeEmails: string[]
  onSelect: (email: string) => void
  fieldLabel: string
  /** Dismisses the panel when the composer itself closes. */
  formOpen: boolean
}

function ContactPicker({
  contacts,
  excludeEmails,
  onSelect,
  fieldLabel,
  formOpen,
}: ContactPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (formOpen) return
    setOpen(false)
    setQuery('')
  }, [formOpen])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent): void => {
      const el = rootRef.current
      if (el && e.target instanceof Node && el.contains(e.target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open])

  /** Contacts are loaded app-wide in App.tsx, so this filters in memory. */
  const options = useMemo(() => {
    const alreadyAdded = new Set(excludeEmails.map((e) => e.toLowerCase()))
    const trimmed = query.trim().toLowerCase()
    return contacts
      .filter(
        (c) =>
          c.email &&
          emailRegex.test(c.email) &&
          !alreadyAdded.has(c.email.toLowerCase()),
      )
      .filter((c) => {
        if (!trimmed) return true
        const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase()
        return name.includes(trimmed) || c.email.toLowerCase().includes(trimmed)
      })
      .slice(0, 50)
  }, [contacts, query, excludeEmails])

  const handleSelect = (email: string): void => {
    onSelect(email)
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="ecf-contact-picker" ref={rootRef}>
      <button
        type="button"
        className="ecf-add-contact-btn"
        aria-label={`Add ${fieldLabel} recipient from contacts`}
        title="Add recipient from contacts"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
      >
        <i className="mgc mgc_user_add_2_fill" aria-hidden />
      </button>
      {open ? (
        <div className="ecf-contact-picker-menu">
          <p className="ecf-contact-picker-target">Adding to {fieldLabel}</p>
          <input
            type="text"
            className="ecf-contact-picker-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts..."
            aria-label="Search contacts"
            autoFocus
          />
          {options.length === 0 ? (
            <p className="ecf-contact-picker-empty">
              {query.trim()
                ? 'No matching contacts.'
                : 'No contacts with an email address.'}
            </p>
          ) : (
            <div
              className="ecf-contact-picker-list"
              role="listbox"
              aria-label="Contacts"
            >
              {options.map((c) => {
                const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
                return (
                  <button
                    key={c.contact_id ?? c.email}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="ecf-contact-picker-item"
                    onClick={() => handleSelect(c.email)}
                  >
                    <span className="ecf-contact-picker-name">
                      {name || c.email}
                    </span>
                    {name ? (
                      <span className="ecf-contact-picker-email">
                        {c.email}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export type EmailComposeFormProps = {
  show: boolean
  draftMessageId?: string
  recipientEmail: string
  initialSubject?: string
  initialBody?: string
  initialTo?: string[]
  initialCc?: string[]
  initialBcc?: string[]
  initialExpandCcRow?: boolean
  initialExpandBccRow?: boolean
  initialAttachments?: EmailAttachmentMeta[]
  modalTitle?: string
  variant: 'inline' | 'modal'
  onDraftChange?: (draft: Partial<EmailComposerDraft>) => void
  onClose: () => void
  onSendSuccess: (payload: {
    to: string[]
    cc: string[]
    bcc: string[]
    attachments?: EmailAttachmentMeta[]
    subject: string
    body: string
    draftMessageId?: string
  }) => void
}

const COMPOSE_EMOJIS = ['😀', '😁', '😊', '😍', '👍', '🙏', '🎉', '❤️'] as const

const ImageToolbarIcon = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <rect
      x="2"
      y="3"
      width="12"
      height="10"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <circle cx="5.5" cy="6.5" r="1" fill="currentColor" />
    <path
      d="M2.8 11.2L6 8.6L8.4 10.4L11 7.6L13.2 11.2"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
)

const FONT_SIZE_COMMAND: Record<RichTextEditorFontSize, string> = {
  '10 pt': '10pt',
  '12 pt': '12pt',
  '14 pt': '14pt',
  '16 pt': '16pt',
  '18 pt': '18pt',
}

const ALIGN_COMMAND: Record<TextAlignment, RteCommandName> = {
  left: 'justifyLeft',
  center: 'justifyCenter',
  right: 'justifyRight',
  justify: 'justifyFull',
}

const TOOLBAR_COMMAND: Partial<
  Record<RichTextEditorToolbarItemId, RteCommandName>
> = {
  undo: 'undo',
  redo: 'redo',
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikeThrough',
  bulletList: 'insertUnorderedList',
  orderedList: 'insertOrderedList',
  indentLeft: 'outdent',
  indentRight: 'indent',
}

const EmailComposeForm: React.FC<EmailComposeFormProps> = ({
  show,
  draftMessageId,
  recipientEmail,
  initialSubject,
  initialBody,
  initialTo,
  initialCc,
  initialBcc,
  initialExpandCcRow,
  initialExpandBccRow,
  initialAttachments,
  modalTitle = 'Compose email',
  variant,
  onDraftChange,
  onClose,
  onSendSuccess,
}) => {
  const rteId = useId()
  const rteRef = useRef<RichTextEditorComponent>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const emojiDropdownRef = useRef<HTMLDivElement>(null)
  const toRowRef = useRef<RecipientChipRowHandle>(null)
  const ccRowRef = useRef<RecipientChipRowHandle>(null)
  const bccRowRef = useRef<RecipientChipRowHandle>(null)
  const [toList, setToList] = useState<string[]>([])
  const [ccList, setCcList] = useState<string[]>([])
  const [bccList, setBccList] = useState<string[]>([])
  const [showCcRow, setShowCcRow] = useState(false)
  const [showBccRow, setShowBccRow] = useState(false)
  const [activeRecipientRow, setActiveRecipientRow] = useState<
    'to' | 'cc' | 'bcc'
  >('to')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [, setFontSize] = useState<RichTextEditorFontSize>('14 pt')
  const [, setParagraphAlign] = useState<TextAlignment>('left')
  const [emojiMenuOpen, setEmojiMenuOpen] = useState(false)
  const [attachments, setAttachments] = useState<EmailAttachmentMeta[]>([])
  const wasOpenRef = useRef(false)
  const signatureAppliedRef = useRef(false)
  const [composeSettings, setComposeSettings] =
    useState<EmailSettingsData | null>(null)
  const composeAssistSettings = normalizeComposeAssistSettings(composeSettings)
  const composeAssistSettingsRef = useRef(composeAssistSettings)
  composeAssistSettingsRef.current = composeAssistSettings
  const [errors, setErrors] = useState<{
    to?: string
    subject?: string
    body?: string
  }>({})

  const plainLength = useMemo(() => stripHtmlForCount(body).length, [body])

  const contacts = useAppSelector((state) => state.contacts.data)
  const contactList = useMemo(() => contacts ?? [], [contacts])

  const {
    isLoading: aiLoading,
    error: aiError,
    run: runAi,
    reset: resetAi,
  } = useAiTextAssist()
  const [aiModeOpen, setAiModeOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiRefineActive, setAiRefineActive] = useState(false)
  const [aiPreviewOpen, setAiPreviewOpen] = useState(false)
  const [aiPreviewHtml, setAiPreviewHtml] = useState('')
  const [aiFeatureKey, setAiFeatureKey] = useState('custom')
  const [aiValidationError, setAiValidationError] = useState<string | null>(
    null,
  )
  const [rteReadyTick, setRteReadyTick] = useState(0)
  const aiPromptInputRef = useRef<HTMLInputElement>(null)
  const lastAiRequestRef = useRef<{
    userText: string
    featureKey: string
  } | null>(null)
  const lastAiResultPlainRef = useRef('')
  const aiModeOpenRef = useRef(false)
  aiModeOpenRef.current = aiModeOpen

  const closeAiMode = useCallback((): void => {
    setAiModeOpen(false)
    setAiPrompt('')
    setAiRefineActive(false)
    setAiPreviewOpen(false)
    setAiPreviewHtml('')
    setAiFeatureKey('custom')
    setAiValidationError(null)
    lastAiRequestRef.current = null
    lastAiResultPlainRef.current = ''
    resetAi()
  }, [resetAi])

  const openAiMode = useCallback((): void => {
    setEmojiMenuOpen(false)
    setAiModeOpen(true)
    setAiValidationError(null)
    setErrors((e) => ({ ...e, body: undefined }))
    window.setTimeout(() => aiPromptInputRef.current?.focus(), 0)
  }, [])

  const runPreviewGeneration = useCallback(
    (userText: string, featureKey: string): void => {
      lastAiRequestRef.current = { userText, featureKey }
      setAiFeatureKey(featureKey)
      setAiPreviewOpen(true)
      setAiPreviewHtml('')
      setAiValidationError(null)
      void runAi(userText, featureKey).then((result) => {
        if (!result) {
          logger.error('AI text assist returned no content', { featureKey })
          return
        }
        lastAiResultPlainRef.current = result
        setAiPreviewHtml(result.replaceAll('\n', '<br/>'))
      })
    },
    [runAi],
  )

  const handleAiAction = useCallback(
    (featureKey: string): void => {
      const previewText = stripHtmlForCount(aiPreviewHtml)
      if (aiPreviewOpen && previewText) {
        runPreviewGeneration(previewText, featureKey)
        return
      }
      const rte = rteRef.current
      if (!rte) {
        logger.error('AI text assist skipped', {
          featureKey,
          reason: 'Editor is not ready.',
        })
        return
      }
      const panel = rte.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      const sel = panel?.ownerDocument?.defaultView?.getSelection?.()
      const selectedText = sel?.toString?.()?.trim()
      const signatureHtml = composeSettings?.signature_enabled
        ? composeSettings.signature
        : ''
      const userText =
        selectedText ||
        stripSignatureFromPlainText(stripHtmlForCount(body), signatureHtml)
      if (!userText) {
        logger.error('AI text assist skipped', {
          featureKey,
          reason: 'Editor text is empty.',
          hasSelection: Boolean(selectedText),
        })
        setAiPreviewOpen(true)
        setAiValidationError('Add message text before using AI.')
        return
      }
      runPreviewGeneration(userText, featureKey)
    },
    [aiPreviewHtml, aiPreviewOpen, body, composeSettings, runPreviewGeneration],
  )

  const handleAiEnhanceSelect = useCallback(
    (e: MenuEventArgs): void => {
      const selected = e.item.text?.trim() || ''
      const match = AI_ENHANCE_ACTIONS.find(
        (action) => action.label.toLowerCase() === selected.toLowerCase(),
      )
      handleAiAction(match?.key || selected.toLowerCase().replace(/\s+/g, '_'))
    },
    [handleAiAction],
  )

  const handleAiEnhancePopupOpen = useCallback(
    (args: { element?: HTMLElement }): void => {
      window.requestAnimationFrame(() => {
        const popup = args.element?.parentElement
        const btn = document.querySelector(
          '.ecf-ai-preview .ai-dropdown',
        ) as HTMLElement | null
        if (!popup || !btn) return
        const rect = btn.getBoundingClientRect()
        popup.style.top = `${rect.top - popup.offsetHeight - 4}px`
        popup.style.left = `${rect.left}px`
      })
    },
    [],
  )

  const handleAiPromptSubmit = useCallback((): void => {
    const prompt = aiPrompt.trim()
    if (!prompt) {
      setAiPreviewOpen(true)
      setAiValidationError('Describe the message to generate.')
      return
    }
    const signatureHtml = composeSettings?.signature_enabled
      ? composeSettings.signature
      : ''
    const draftText = stripSignatureFromPlainText(
      stripHtmlForCount(body),
      signatureHtml,
    )
    const userText =
      draftText && draftText !== AI_SLASH_TRIGGER
        ? `${prompt}\n\nExisting draft:\n${draftText}`
        : prompt
    runPreviewGeneration(userText, aiRefineActive ? 'rewrite' : 'custom')
  }, [aiPrompt, aiRefineActive, body, composeSettings, runPreviewGeneration])

  const handleAiRegenerate = useCallback((): void => {
    const lastRequest = lastAiRequestRef.current
    if (!lastRequest) {
      handleAiPromptSubmit()
      return
    }
    runPreviewGeneration(lastRequest.userText, lastRequest.featureKey)
  }, [handleAiPromptSubmit, runPreviewGeneration])

  const handleInsertAiPreview = useCallback((): void => {
    if (!aiPreviewHtml) return
    const signature = composeSettings?.signature_enabled
      ? composeSettings.signature?.trim() || ''
      : ''
    const aiPlain =
      lastAiResultPlainRef.current || aiPreviewHtmlToPlain(aiPreviewHtml)
    const includeGreeting = aiFeatureKey === 'custom' && !aiRefineActive
    const next = formatEmailBodyFromAiInsert({
      aiPlainText: aiPlain,
      recipientEmails: toList,
      contacts: contactList,
      signatureHtml: signature,
      includeGreeting,
    })
    setBody(next)
    if (rteRef.current) rteRef.current.value = next
    closeAiMode()
  }, [
    aiFeatureKey,
    aiPreviewHtml,
    aiRefineActive,
    closeAiMode,
    composeSettings,
    contactList,
    toList,
  ])

  /**
   * The picker button lives on the To row (per the design) but fills whichever
   * recipient field was last focused, falling back to To when that row is hidden.
   */
  const targetRecipientRow =
    (activeRecipientRow === 'cc' && showCcRow) ||
    (activeRecipientRow === 'bcc' && showBccRow)
      ? activeRecipientRow
      : 'to'

  const recipientTarget = {
    to: { label: 'To', emails: toList, setList: setToList },
    cc: { label: 'Cc', emails: ccList, setList: setCcList },
    bcc: { label: 'Bcc', emails: bccList, setList: setBccList },
  }[targetRecipientRow]

  const focusRecipientRow = (row: 'to' | 'cc' | 'bcc'): void => {
    const ref = row === 'cc' ? ccRowRef : row === 'bcc' ? bccRowRef : toRowRef
    // Defer so a newly revealed Cc/Bcc row has mounted before we focus it.
    window.setTimeout(() => {
      ref.current?.focus()
    }, 0)
  }

  const openCcRow = (): void => {
    setShowCcRow(true)
    setActiveRecipientRow('cc')
    focusRecipientRow('cc')
  }

  const openBccRow = (): void => {
    setShowBccRow(true)
    setActiveRecipientRow('bcc')
    focusRecipientRow('bcc')
  }

  const handlePickContact = (email: string): void => {
    recipientTarget.setList((prev) =>
      prev.includes(email) ? prev : [...prev, email],
    )
    if (targetRecipientRow === 'to') {
      setErrors((e) => ({ ...e, to: undefined }))
    }
    // Keep the cursor in the same recipient field after picking.
    focusRecipientRow(targetRecipientRow)
  }

  const exec = useCallback(
    (command: RteCommandName, value?: RteCommandValue): void => {
      const rte = rteRef.current
      if (!rte) return
      focusRte(rte)
      const panel = rte.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      normalizeBrowserSelectionInRteEditPanel(panel)
      const run = (): void => {
        if (value !== undefined) {
          rte.executeCommand(command, value)
        } else {
          rte.executeCommand(command)
        }
      }
      try {
        run()
      } catch {
        normalizeBrowserSelectionInRteEditPanel(panel)
        try {
          run()
        } catch {
          /* selection edge cases */
        }
      }
    },
    [],
  )

  const handleToolbarAction = useCallback(
    (actionId: RichTextEditorToolbarItemId): void => {
      if (actionId === 'blockquote') {
        exec('formatBlock', 'blockquote')
        return
      }
      if (actionId === 'highlight') {
        exec('backColor', '#FFF3B0')
        return
      }
      if (actionId === 'link') {
        const url = window.prompt('Enter link URL')
        if (!url) return
        exec('createLink', { url })
        return
      }
      const command = TOOLBAR_COMMAND[actionId]
      if (command) exec(command)
    },
    [exec],
  )

  const handleFontSizeChange = useCallback(
    (nextSize: RichTextEditorFontSize): void => {
      setFontSize(nextSize)
      exec('fontSize', FONT_SIZE_COMMAND[nextSize])
    },
    [exec],
  )

  const handleAlignmentChange = useCallback(
    (nextAlign: TextAlignment): void => {
      setParagraphAlign(nextAlign)
      exec(ALIGN_COMMAND[nextAlign])
    },
    [exec],
  )

  useEffect(() => {
    if (!show) {
      wasOpenRef.current = false
      signatureAppliedRef.current = false
      return
    }
    if (wasOpenRef.current) return
    wasOpenRef.current = true
    const to = initialTo?.length
      ? [...initialTo]
      : recipientEmail
        ? [recipientEmail]
        : []
    setToList(to)
    const nextCc = initialCc ? [...initialCc] : []
    const nextBcc = initialBcc ? [...initialBcc] : []
    setCcList(nextCc)
    setBccList(nextBcc)
    setShowCcRow(nextCc.length > 0 || Boolean(initialExpandCcRow))
    setShowBccRow(nextBcc.length > 0 || Boolean(initialExpandBccRow))
    setActiveRecipientRow('to')
    setSubject(initialSubject ?? '')
    setBody(initialBody ?? '')
    setAttachments(initialAttachments ? [...initialAttachments] : [])
    setErrors({})
    setFontSize('14 pt')
    setParagraphAlign('left')
    setAiModeOpen(false)
    setAiPrompt('')
    setAiRefineActive(false)
    setAiPreviewOpen(false)
    setAiPreviewHtml('')
    setAiFeatureKey('custom')
    setAiValidationError(null)
    setEmojiMenuOpen(false)
  }, [
    recipientEmail,
    initialSubject,
    initialBody,
    initialTo,
    initialCc,
    initialBcc,
    initialExpandCcRow,
    initialExpandBccRow,
    initialAttachments,
    show,
  ])

  useEffect(() => {
    if (!show) return
    let active = true
    void (async () => {
      try {
        const settings = await requestEmailSettings()
        if (!active) return
        setComposeSettings(settings)
      } catch {
        if (active) setComposeSettings(null)
      }
    })()
    return () => {
      active = false
    }
  }, [show])

  useEffect(() => {
    if (!show) return
    if (signatureAppliedRef.current) return
    if (draftMessageId || (initialBody && initialBody.trim())) return
    if (!composeSettings?.signature_enabled) return
    const signature = composeSettings.signature?.trim()
    if (!signature) return
    signatureAppliedRef.current = true
    setBody((prev) =>
      stripHtmlForCount(prev) ? prev : `<p><br></p><p><br></p>${signature}`,
    )
  }, [show, draftMessageId, initialBody, composeSettings])

  const applyComposeAssist = useCallback((): boolean => {
    const rte = rteRef.current
    if (!rte || !show) return false
    return applyComposeAssistToRte(rte, composeAssistSettingsRef.current)
  }, [show])

  const handleBodyKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && aiModeOpenRef.current) {
        e.preventDefault()
        closeAiMode()
        return
      }
      if (e.key !== AI_SLASH_TRIGGER || e.ctrlKey || e.metaKey || e.altKey) {
        return
      }
      if (aiModeOpenRef.current) return
      const panel = e.currentTarget as HTMLElement
      const firstLine = (panel.innerText ?? '').split('\n')[0]?.trim() ?? ''
      if (firstLine === '') {
        e.preventDefault()
        openAiMode()
      }
    },
    [closeAiMode, openAiMode],
  )

  const handleRteCreated = useCallback((): void => {
    applyComposeAssist()
    window.setTimeout(() => {
      applyComposeAssist()
    }, 0)
    setRteReadyTick((tick) => tick + 1)
  }, [applyComposeAssist])

  useEffect(() => {
    if (!show) return
    let attached: HTMLElement | undefined
    const tryAttach = (): void => {
      if (attached) return
      const panel = rteRef.current?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!panel) return
      panel.addEventListener('keydown', handleBodyKeyDown)
      attached = panel
    }
    tryAttach()
    const timeoutId = window.setTimeout(tryAttach, 0)
    return () => {
      window.clearTimeout(timeoutId)
      attached?.removeEventListener('keydown', handleBodyKeyDown)
    }
  }, [handleBodyKeyDown, rteReadyTick, show])

  useEffect(() => {
    if (!show) return
    if (applyComposeAssist()) return
    const frameId = window.requestAnimationFrame(() => {
      applyComposeAssist()
    })
    const timeoutId = window.setTimeout(() => {
      applyComposeAssist()
    }, 100)
    return () => {
      window.cancelAnimationFrame(frameId)
      window.clearTimeout(timeoutId)
    }
  }, [show, composeAssistSettings, applyComposeAssist])

  useEffect(() => {
    if (!show) {
      setAiModeOpen(false)
      setAiPrompt('')
      setAiRefineActive(false)
      setAiPreviewOpen(false)
      setAiPreviewHtml('')
      setAiFeatureKey('custom')
      setAiValidationError(null)
      setEmojiMenuOpen(false)
    }
  }, [show])

  useEffect(() => {
    if (!emojiMenuOpen) return
    const onPointerDown = (e: PointerEvent): void => {
      const el = emojiDropdownRef.current
      if (el && e.target instanceof Node && el.contains(e.target)) return
      setEmojiMenuOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setEmojiMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [emojiMenuOpen])

  const insertEmoji = useCallback(
    (emoji: string): void => {
      exec('insertText', emoji)
      setEmojiMenuOpen(false)
    },
    [exec],
  )

  const handleEditorChange = (args: { value: string }): void => {
    const plain = stripHtmlForCount(args.value)
    if (!aiModeOpenRef.current && plain === AI_SLASH_TRIGGER) {
      const rte = rteRef.current
      if (rte) rte.value = ''
      setBody('')
      openAiMode()
      return
    }
    setBody(args.value)
    if (args.value) {
      setErrors((e) => ({ ...e, body: undefined }))
    }
  }

  const validate = (): boolean => {
    const next: typeof errors = {}
    if (toList.length === 0) {
      next.to = 'Add at least one recipient'
    }
    if (!subject.trim()) {
      next.subject = 'Subject is required'
    }
    if (!stripHtmlForCount(body)) {
      next.body = 'Message is required'
    } else if (plainLength > BODY_MAX_CHARS) {
      next.body = `Message must be ${BODY_MAX_CHARS} characters or less`
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSend = (): void => {
    if (!validate()) return
    onSendSuccess({
      to: toList,
      cc: ccList,
      bcc: bccList,
      attachments,
      subject,
      body,
      draftMessageId,
    })
  }

  useEffect(() => {
    if (!show) return
    onDraftChange?.({
      recipientEmail: toList[0] ?? recipientEmail ?? '',
      to: toList,
      cc: ccList,
      bcc: bccList,
      attachments,
      subject,
      body,
      modalTitle,
    })
  }, [
    show,
    toList,
    ccList,
    bccList,
    attachments,
    subject,
    body,
    modalTitle,
    recipientEmail,
    onDraftChange,
  ])

  const handleAttachmentClick = (): void => {
    fileInputRef.current?.click()
  }

  const formatAttachmentSizeLabel = (size: number): string => {
    const mb = size / (1024 * 1024)
    if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)}MB`
    const kb = size / 1024
    if (kb >= 1) return `${Math.max(1, Math.round(kb))}KB`
    return `${Math.max(1, size)}B`
  }

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '')
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })

  const handleInsertImageClick = (): void => {
    imageInputRef.current?.click()
  }

  const handleImageInputChange: React.ChangeEventHandler<
    HTMLInputElement
  > = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !file.type.startsWith('image/')) return

    try {
      const url = await readFileAsDataUrl(file)
      if (!url) return
      exec('insertImage', {
        url,
        altText: file.name,
        cssClass: 'e-imginline',
      })
    } catch {
      /* ignore read failures */
    }
  }

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : ''
        resolve(
          result.includes(',') ? result.slice(result.indexOf(',') + 1) : result,
        )
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })

  const handleAttachmentInputChange: React.ChangeEventHandler<
    HTMLInputElement
  > = async (e) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length === 0) return

    const newAttachments = await Promise.all(
      files.map(async (f) => ({
        id: `${f.name}-${f.size}-${f.lastModified}`,
        name: f.name,
        sizeLabel: formatAttachmentSizeLabel(f.size),
        mimeType: f.type || undefined,
        content: await readFileAsBase64(f),
      })),
    )

    setAttachments((prev) => {
      const next = [...prev]
      newAttachments.forEach((attachment) => {
        if (next.some((a) => a.id === attachment.id)) return
        next.push(attachment)
      })
      return next
    })
  }

  const removeAttachment = (id: string): void => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const toolbarClass =
    variant === 'inline'
      ? 'ecf-card ecf-card--inline'
      : 'ecf-card ecf-card--modal'

  return (
    <div className={toolbarClass}>
      {variant === 'inline' && (
        <div className="ecf-inline-header">
          <button
            type="button"
            className="ecf-back-btn"
            onClick={onClose}
            aria-label="Close composer"
          >
            <i className="mgc mgc_left_line" aria-hidden />
          </button>
          <span className="ecf-inline-title">{modalTitle}</span>
        </div>
      )}
      <div className="ecf-header-fields">
        <RecipientChipRow
          ref={toRowRef}
          label="To:"
          emails={toList}
          onChange={setToList}
          placeholder="Add recipient email..."
          onFocus={() => setActiveRecipientRow('to')}
          trailing={
            <div className="ecf-cc-bcc-toggles">
              {!showCcRow && (
                <button
                  type="button"
                  className="ecf-cc-bcc-btn"
                  aria-expanded={showCcRow}
                  aria-controls="email-compose-cc-row"
                  onClick={openCcRow}
                >
                  Cc
                </button>
              )}
              {!showBccRow && (
                <button
                  type="button"
                  className="ecf-cc-bcc-btn"
                  aria-expanded={showBccRow}
                  aria-controls="email-compose-bcc-row"
                  onClick={openBccRow}
                >
                  Bcc
                </button>
              )}
              <ContactPicker
                contacts={contactList}
                excludeEmails={recipientTarget.emails}
                fieldLabel={recipientTarget.label}
                formOpen={show}
                onSelect={handlePickContact}
              />
            </div>
          }
        />
        {errors.to && <p className="ecf-field-error">{errors.to}</p>}
        {showCcRow && (
          <div id="email-compose-cc-row">
            <RecipientChipRow
              ref={ccRowRef}
              label="Cc:"
              emails={ccList}
              onChange={setCcList}
              placeholder="Add Cc recipient..."
              onFocus={() => setActiveRecipientRow('cc')}
            />
          </div>
        )}
        {showBccRow && (
          <div id="email-compose-bcc-row">
            <RecipientChipRow
              ref={bccRowRef}
              label="Bcc:"
              emails={bccList}
              onChange={setBccList}
              placeholder="Add Bcc recipient..."
              onFocus={() => setActiveRecipientRow('bcc')}
            />
          </div>
        )}
        <div className="ecf-recipient-row">
          <span className="ecf-field-label">Subject:</span>
          <div className="ecf-subject-field">
            <input
              type="text"
              className="ecf-subject-input"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value)
                if (e.target.value) {
                  setErrors((er) => ({ ...er, subject: undefined }))
                }
              }}
              placeholder="Enter subject..."
            />
          </div>
        </div>
        {errors.subject && <p className="ecf-field-error">{errors.subject}</p>}
      </div>

      <div className="ecf-body-block">
        <div className="ecf-rte-wrap">
          <RichTextEditorComponent
            id={`email-compose-rte-${rteId}`}
            ref={rteRef}
            created={handleRteCreated}
            iframeSettings={{ enable: false }}
            height={variant === 'inline' ? '100%' : '280px'}
            toolbarSettings={{ items: [] }}
            insertImageSettings={{
              saveFormat: 'Base64',
              display: 'inline',
            }}
            value={body}
            change={handleEditorChange}
            placeholder='Type your message here or type "/" to enable AI functionality'
            enableXhtml={false}
          >
            <Inject services={[Toolbar, Link, HtmlEditor, Image]} />
          </RichTextEditorComponent>
        </div>
        {attachments.length > 0 && (
          <div className="ecf-attachments-list">
            {attachments.map((att) => (
              <div key={att.id} className="ecf-attachment-chip">
                <div className="ecf-attachment-meta">
                  <p className="ecf-attachment-name">{att.name}</p>
                  <span className="ecf-attachment-size">{att.sizeLabel}</span>
                </div>
                <button
                  type="button"
                  className="ecf-attachment-remove"
                  aria-label={`Remove ${att.name}`}
                  onClick={() => removeAttachment(att.id)}
                >
                  <i className="mgc mgc_close_line" aria-hidden />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="ecf-hipaa-row">
          <div className="ecf-hipaa-left">
            <i className="mgc mgc_lock_fill ecf-hipaa-icon" aria-hidden />
            <span className="ecf-hipaa-text">
              This Email is HIPAA Compliant
            </span>
          </div>
          <span className="ecf-char-count">
            {plainLength}/{BODY_MAX_CHARS}
          </span>
        </div>
        {errors.body && (
          <p className="ecf-field-error ecf-body-error">{errors.body}</p>
        )}
      </div>

      <div className="ecf-footer-toolbar">
        {aiModeOpen && (
          <div className="ecf-ai-compose">
            {(aiPreviewOpen ||
              Boolean(aiError) ||
              Boolean(aiValidationError)) && (
              <div className="ecf-ai-preview">
                <div className="ecf-ai-preview-header">
                  <span className="mgc_pencil_2_ai_fill" />
                  {aiFeatureKey === 'custom'
                    ? 'Generating results'
                    : aiTextActions.find(
                        (action) => action.key === aiFeatureKey,
                      )?.label || 'Generating results'}
                </div>
                <div className="ecf-ai-preview-body">
                  {aiLoading ? (
                    <div className="ecf-ai-preview-loading">
                      <i
                        className="mgc mgc_loading_3_line ecf-spin"
                        aria-hidden
                      />
                      Generating…
                    </div>
                  ) : aiError || aiValidationError ? (
                    <p className="ecf-ai-preview-error">
                      {aiError || aiValidationError}
                    </p>
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeEmailHtml(aiPreviewHtml),
                      }}
                    />
                  )}
                </div>
                {!aiLoading &&
                !aiError &&
                !aiValidationError &&
                aiPreviewHtml ? (
                  <div className="ecf-ai-preview-actions">
                    <div className="ecf-ai-preview-actions-left">
                      <button
                        type="button"
                        className="ecf-ai-text-btn"
                        onClick={handleAiRegenerate}
                      >
                        <span className="ti ti-refresh" aria-hidden />
                        Regenerate
                      </button>
                      <DropDownButtonComponent
                        select={handleAiEnhanceSelect}
                        open={handleAiEnhancePopupOpen}
                        className="ai-button-text ai-dropdown"
                        cssClass="generating-results-dropdown-elements"
                        items={AI_ENHANCE_DROPDOWN_ITEMS}
                      >
                        <span className="mgc_magic_3_fill button-icon" />
                        Enhance
                      </DropDownButtonComponent>
                    </div>
                    <Button
                      size="sm"
                      className="button-primary"
                      onClick={handleInsertAiPreview}
                    >
                      {stripHtmlForCount(body) ? 'Replace' : 'Insert'}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
            <div className="ecf-ai-buttons">
              <button
                type="button"
                className={`ecf-ai-button${aiRefineActive ? ' active' : ''}`}
                onClick={() => {
                  setAiRefineActive((active) => !active)
                  window.setTimeout(() => aiPromptInputRef.current?.focus(), 0)
                }}
              >
                <span className="mgc_pencil_2_ai_fill" aria-hidden />
                Refine
              </button>
              {AI_EXTRA_ACTIONS.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="ecf-ai-button"
                  disabled={aiLoading}
                  onClick={() => handleAiAction(action.key)}
                >
                  <i className={action.icon} aria-hidden />
                  {action.label}
                </button>
              ))}
            </div>
            <div className="ecf-ai-prompt-row">
              <div className="ecf-ai-inline-display">
                <Button
                  size="sm"
                  className="button-secondary"
                  leftIcon={<span className="mgc_pencil_2_ai_fill" />}
                  rightIcon={<span className="mgc_close_line" />}
                  onClick={closeAiMode}
                  aria-label="Close AI"
                />
                <span>/AI</span>
              </div>
              <input
                ref={aiPromptInputRef}
                type="text"
                className="ecf-ai-prompt-input"
                value={aiPrompt}
                placeholder={
                  aiRefineActive ? AI_REFINE_PLACEHOLDER : AI_PROMPT_PLACEHOLDER
                }
                aria-label={
                  aiRefineActive ? AI_REFINE_PLACEHOLDER : AI_PROMPT_PLACEHOLDER
                }
                disabled={aiLoading}
                onChange={(e) => {
                  setAiPrompt(e.target.value)
                  if (e.target.value) {
                    setAiValidationError(null)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAiPromptSubmit()
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    closeAiMode()
                  }
                }}
              />
            </div>
          </div>
        )}
        <div className="ecf-footer-inner">
          <RichTextEditor
            variant="email-fax"
            toolbarOnly
            onToolbarAction={handleToolbarAction}
            onFontSizeChange={handleFontSizeChange}
            onAlignmentChange={handleAlignmentChange}
          />
          <div className="ecf-footer-actions">
            <div className="ecf-ai-dropdown">
              <button
                type="button"
                className={`ds-rich-text-editor__icon-btn ds-rich-text-editor__icon-btn--magic${
                  aiModeOpen ? ' ecf-ai-wand--active' : ''
                }`}
                aria-label="AI assist"
                aria-pressed={aiModeOpen}
                disabled={aiLoading}
                onClick={() => {
                  setEmojiMenuOpen(false)
                  if (aiModeOpen) closeAiMode()
                  else openAiMode()
                }}
              >
                <WriteAiIcon width={16} height={16} aria-hidden="true" />
              </button>
            </div>
            <div className="ecf-ai-dropdown" ref={emojiDropdownRef}>
              <button
                type="button"
                className="ds-rich-text-editor__icon-btn"
                aria-label="Emoji"
                aria-expanded={emojiMenuOpen}
                aria-haspopup="listbox"
                onClick={() => {
                  setEmojiMenuOpen((open) => !open)
                }}
              >
                <EmojiSmileIcon width={16} height={16} aria-hidden="true" />
              </button>
              {emojiMenuOpen ? (
                <div
                  className="ecf-emoji-dropdown-menu"
                  role="listbox"
                  aria-label="Emojis"
                >
                  {COMPOSE_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      role="option"
                      className="ecf-emoji-dropdown-item"
                      aria-label={`Insert ${emoji}`}
                      onClick={() => insertEmoji(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="ds-rich-text-editor__icon-btn"
              aria-label="Attach file"
              onClick={handleAttachmentClick}
            >
              <AttachmentIcon width={16} height={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ds-rich-text-editor__icon-btn"
              aria-label="Insert image"
              onClick={handleInsertImageClick}
            >
              <ImageToolbarIcon />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="ecf-file-input"
              onChange={handleAttachmentInputChange}
              tabIndex={-1}
              aria-hidden
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="ecf-file-input"
              onChange={handleImageInputChange}
              tabIndex={-1}
              aria-hidden
            />
            <button
              type="button"
              className={`ds-rich-text-editor__send-btn${
                plainLength > 0 ? ' ds-rich-text-editor__send-btn--ready' : ''
              }`}
              aria-label="Send email"
              onClick={handleSend}
            >
              <SendFillIcon width={14} height={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailComposeForm
